import {
    OktaFactorState,
    MfaManageStep,
    OktaFactor,
    OktaFactorProfile,
    FetchState,
    OktaFactorType,
    UserFeedbackEventAction
} from "@/shared/types";
import * as storeHelper from "@/shared/storeHelper";
import * as UserService from "@/shared/api/userService";
import _ from "lodash";
import { defineStore } from "pinia";
import { useNotifierStore } from "@/shared/store/notifier";
import { useUserFeedbackStore } from "@/employee/store/userFeedback";

const maskEmail = (email: string) => {
    const arr = email.split("@");
    return arr[0][0] + ".".repeat(3) + arr[0].slice(-1) + "@" + arr[1];
};

export const initialState: OktaFactorState = {
    step: MfaManageStep.Manage,
    factors: [],
    selectedFactor: null,
    totpData: {},
    isEditingProfile: false,
    factorsLoadFetchState: FetchState.Uninitialized
};

export const useOktaFactorStore = defineStore("oktaFactor", {
    state: () => (_.cloneDeep(initialState)),
    getters: {
        totpSecret: state => _.get(state, "totpData.activation.sharedSecret", "") as string,
        qrCode: state => _.get(state, "totpData.activation._links.qrcode.href", "") as string,
        isFactorsLoaded: state => state.factorsLoadFetchState === FetchState.Loaded
    },
    actions: {
        setStep (s: MfaManageStep) {
            this.step = s;
        },
        setFactors (factors: OktaFactor[]) {
            this.factors = factors;
        },
        setSelectedFactor (factor: OktaFactor) {
            this.selectedFactor = factor;
        },
        setTotpData (data: { [key: string]: any }) {
            this.totpData = data;
        },
        setIsEditingProfile (value: boolean) {
            this.isEditingProfile = value;
        },
        setFactorsLoadFetchState (fetchState: FetchState) {
            this.factorsLoadFetchState = fetchState;
        },
        loadFactors (): Promise<any> {

            if (this.factorsLoadFetchState !== FetchState.Uninitialized) {
                return Promise.resolve();
            }

            this.setFactorsLoadFetchState(FetchState.Loading);
            return UserService.oktaFactors()
                .then(factors => {
                    this.setFactors(factors.data);
                    this.setFactorsLoadFetchState(FetchState.Loaded);
                })
                .catch(err => {
                    this.setFactorsLoadFetchState(FetchState.Loaded);
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        enrollFactor (profile?: OktaFactorProfile) {
            if (!this.selectedFactor) {
                return;
            }

            const factor = this.selectedFactor;
            if (!factor.factorType) {
                return;
            }

            const request = {
                factorType: factor.factorType,
                profile: { ...profile }
            };

            let factorValue = "";
            switch (this.selectedFactor.factorType) {
                case OktaFactorType.Email:
                    factorValue = maskEmail(profile.email);
                    break;
                case OktaFactorType.Sms:
                case OktaFactorType.Call:
                    factorValue = profile.phoneNumber;
                    break;
            }

            const notifierStore = useNotifierStore();
            const userFeedbackStore = useUserFeedbackStore();
            return UserService.enrollFactor(request)
                .then(response => {
                    this.setFactorsLoadFetchState(FetchState.Uninitialized);
                    if (response.data.status === "active") {
                        this.setSelectedFactor(null);
                        this.setIsEditingProfile(false);
                        this.loadFactors();
                        this.setStep(MfaManageStep.Manage);
                        notifierStore.setSuccess(`${factor.displayName} MFA has been added to your account.`);
                        userFeedbackStore.setUserFeedbackAction(UserFeedbackEventAction.Security);
                    } else {
                        this.selectedFactor.id = response.data.factorId;
                        this.setTotpData(response.data.embedded);
                        notifierStore.setSuccess(`A verification code has been sent to ${factorValue}.`);
                    }

                    return response.data;
                })
                .catch(error => {
                    this.revertFactorStatus(factor.key);
                    storeHelper.handleError({ error, rethrow: false });
                });
        },
        activateFactor (passCode: string) {
            if (!this.selectedFactor) {
                return;
            }

            const factor = this.selectedFactor;
            if (!factor.id) {
                return;
            }
            const notifierStore = useNotifierStore();
            const userFeedbackStore = useUserFeedbackStore();
            return UserService.activateFactor(factor.id, passCode)
                .then(() => {
                    this.setFactorsLoadFetchState(FetchState.Uninitialized);
                    this.setSelectedFactor(null);
                    this.setIsEditingProfile(false);
                    this.setTotpData({});
                    this.loadFactors();
                    this.setStep(MfaManageStep.Manage);
                    notifierStore.setSuccess(`${factor.displayName} MFA has been added to your account.`);
                    userFeedbackStore.setUserFeedbackAction(UserFeedbackEventAction.Security);
                })
                .catch(error => {
                    storeHelper.handleError({ error, rethrow: false });
                });
        },
        resetFactor () {
            if (!this.selectedFactor) {
                return;
            }

            const factor = this.selectedFactor;
            if (!factor.id) {
                return;
            }

            return UserService.resetOktaFactor(factor.id)
                .catch(error => {
                    this.revertFactorStatus(factor.key);
                    storeHelper.handleError({ error, rethrow: false });
                });
        },
        removeFactor () {
            if (!this.selectedFactor) {
                return;
            }

            const factor = this.selectedFactor;
            if (!factor.id) {
                return;
            }
            const notifierStore = useNotifierStore();

            return UserService.removeOktaFactor(factor.id)
                .then(() => {
                    if (!this.isEditingProfile) {
                        this.setFactorsLoadFetchState(FetchState.Uninitialized);
                        this.setSelectedFactor(null);
                        this.loadFactors();
                        this.setStep(MfaManageStep.Manage);
                        notifierStore.setSuccess(`${factor.displayName} MFA has been removed from your account.`);
                    }
                })
                .catch(error => {
                    this.revertFactorStatus(factor.key);
                    storeHelper.handleError({ error, rethrow: false });
                });
        },
        revertFactorStatus (key: string) {
            const factor = this.factors.find(f => f.key === key);
            if (factor) {
                factor.isActive = !factor.isActive;
            }
        },
        clearState () {
            this.setSelectedFactor(null);
            this.setIsEditingProfile(false);
            this.setTotpData({});
            this.setStep(MfaManageStep.Manage);
        },
        updateStep (newStep: MfaManageStep) {
            this.setStep(newStep);
        },
        revertFactor () {
            if (!this.selectedFactor) {
                return;
            }

            const factor = this.selectedFactor;
            if (!factor.id) {
                return;
            }
            return UserService.revert({ factorId: factor.id, factorType: factor.factorType })
                .then(() => {
                    this.setFactorsLoadFetchState(FetchState.Uninitialized);
                })
                .catch(error => {
                    storeHelper.handleError({ error, rethrow: false });
                });
        }
    }
});